import React, { useState, useEffect, useContext } from 'react'
import { Typography, Button, Spinner } from '@telavita-core/react-design-kit'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { useDispatch, useSelector } from 'react-redux'
import { CPF } from 'gerador-validador-cpf'

import { asyncError, authLogin, clearPersons } from 'redux/actions'
import { SignupContext } from 'contexts/SignupContext'

import { history } from 'routers/AppRouter'

import { REGISTER_PHONE_CHECK_PIN, REGISTER, REGISTER_SUCCESS } from 'settings/_routesSettings'
import { PlanGroupCategoryEnum } from 'settings/_planGroupSettings'
import { configMixpanelCookieExpiration } from 'settings/mixpanel'
import { AWAITING_PIN } from 'settings/_personSettings'
import { apiEndpoints } from 'settings/_apiSettings'
import { PATIENT } from 'settings/_profileSettings'
import axios from 'settings/axios'

import { isSimpleSignUpEnabled } from 'utils/isSimpleSignUpEnabled'

import { stepsList } from './Steps'
import Register from '../Register'

import Row from 'components/Row'
import { Column } from 'components'
import { Pin } from 'components/Pin'
import { PinSendedMessage } from 'components/PinSendedMessage'
import { PhoneInput, handleValidPhoneNumber} from 'components/PhoneInput'

const PhoneCheck = () => {

  const {
    firstName,
    lastName,
    email,
    password,
    cpf,
    dateBirth,
    gender,
    planGroup,
    cellPhone,
    setCellPhone,
  } = useContext(SignupContext)

  const { executeRecaptcha } = useGoogleReCaptcha()

  const dispatch = useDispatch()

  const [cellPhoneMsg, setCellPhoneMsg] = useState('')
  const [disableInput, setDisableInput] = useState(false)
  const [editPhone, setEditPhone] = useState(false)
  const [isValid, setIsValid] = useState(false)
  const [pinCode, setPinCode] = useState('')
  const [showPin, setShowPin] = useState(false)
  const [pinSent, setPinSent] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [pinError, setPinError] = useState(false)
  const [resendPinLoading, setResendPinLoading] = useState(false)

  const plans = useSelector(state => state.register.plans)

  const { 
    ENTERPRISE, 
    CAPITATION,
    CORPORATE_LIMITED,
    CORPORATE_UNLIMITED
  } = PlanGroupCategoryEnum
  const isEnterprise = planGroup.category === ENTERPRISE

  const hasSimpleSignUp = isSimpleSignUpEnabled(planGroup.code)

  const hasPlanGroupCode = !!planGroup.code

  useEffect(() => {
    if(!email)
      window.location.href = REGISTER

    // eslint-disable-next-line
  }, [])

  function handlePinInput(value) {
    setIsValid(true)
    setPinCode(value)
  }

  /* 
    Essa configuração do cookie do Mixpanel é necessária para que o time de marketing tenha 
    mais precisão na análise dos dados quando o paciente se cadastra.
  */ 
  function handleMixpanelCookieExpiration() {
    const EXPIRATION_IN_DAYS = 7

    return configMixpanelCookieExpiration(EXPIRATION_IN_DAYS)
  }

  function onCellChange(cellPhone) {
    if (cellPhone) {
      if (handleValidPhoneNumber(cellPhone)) {
        setCellPhone(cellPhone)
        setCellPhoneMsg('')
        setIsValid(true)
      } else {
        setCellPhone(cellPhone)
        setCellPhoneMsg('Ops, telefone inválido!')
        setIsValid(false)
      }
    } else {
      setCellPhone(cellPhone)
      setCellPhoneMsg('Número de telefone é necessário!')
      setIsValid(false)
    }
  }

  function onEditPhone() {
    setCellPhone('')
    setDisableInput(false)
    setEditPhone(true)
    setIsValid(false)
    setShowPin(false)
    setPinError(false)
  }

  function onPrevStep() {
    history.goBack()
  }

  async function onNextStep() {
    setIsLoading(true)
    if (isValid) {
      setIsValid(false)

      const putUrl = `${apiEndpoints.PERSONS}${email}/?profile_role=${PATIENT}`

      let postData = {}
      let clearCellphone = cellPhone.replace('+', '')

      if (editPhone) {
        postData = { cellphone_number: clearCellphone }

        axios.put(putUrl, postData)
          .then(response => {
            if (response.status === 'OK') {
              setCellPhoneMsg('')
              setDisableInput(true)
              handlePin()
            } else {
              setDisableInput(false)
              setEditPhone(true)
              setIsValid(false)
              setShowPin(false)
            }
          })
          .catch(err => {
            dispatch(asyncError(err))

            const { status } = err.response

            if (status === 400 || status === 409 )  {
              setCellPhoneMsg('Celular já cadastrado.')
              setDisableInput(false)
              setEditPhone(true)
              setIsValid(false)
              setShowPin(false)
              setIsLoading(false)
            }
          })
      } else {
        let postUrl = ''
        let planType = ''

        if (!hasPlanGroupCode) {
          postUrl = apiEndpoints.PATIENTS_PARTICULAR
          planType = 'particular'
        } else {
          postUrl = apiEndpoints.PATIENTS_PLAN(getPlanGroupCategory())
          planType = getPlanGroupCategory()
        }

        const token = await executeRecaptcha('signup')
        const formattedCpf = CPF.format(cpf, 'digits')

        const postData = {
          particular: {
            email: email,
            first_name: firstName,
            last_name: lastName,
            status_code: AWAITING_PIN,
            password: password,
            cellphone_number: clearCellphone,
            cpf: formattedCpf,
            date_birth: dateBirth,
            gender: gender.code,
            recaptcha_token: token
          },
          corporate: {
            email: email,
            first_name: firstName,
            last_name: lastName,
            password: password,
            cellphone_number: clearCellphone,
            cpf: formattedCpf,
            date_birth: dateBirth,
            gender: gender.code,
            plan_id: plans[0]?.partner_plan_id,
            recaptcha_token: token
          },
          capitation: {
            email: email,
            first_name: firstName,
            last_name: lastName,
            password: password,
            cellphone_number: clearCellphone,
            cpf: formattedCpf,
            date_birth: dateBirth,
            gender: gender.code,
            plan_id: plans[0]?.partner_plan_id,
            recaptcha_token: token           
          },
          enterprise: {
            email: email,
            first_name: firstName,
            last_name: lastName,
            password: password,
            cellphone_number: clearCellphone,
            plan: {
              plan_id: plans[0]?.partner_plan_id,
              card_number: plans[0]?.card_number,
            },
            cpf: formattedCpf,
            date_birth: dateBirth,
            gender: gender.code,
            recaptcha_token: token
          }
        }

        axios.post(postUrl, postData[planType])
          .then(response => {
            if (response.status === 'OK') {
              setCellPhoneMsg('')
              setDisableInput(true)
              handlePin()
              handleMixpanelCookieExpiration()
              history.push(REGISTER_PHONE_CHECK_PIN)
            } else {
              setDisableInput(false)
              setIsValid(false)
              setShowPin(false)
            }
          })
          .catch(err => {
            dispatch(asyncError(err))

            const { status } = err.response

            if (status === 400 || status === 409 )  {
              setCellPhoneMsg('Celular já cadastrado.')
              setDisableInput(false)
              setIsValid(false)
              setShowPin(false)
              setIsLoading(false)
            }

            if(status === 403) {
              setCellPhoneMsg('Não foi possível realizar seu cadastro. Tente novamente.')
            }

            setIsLoading(false)
          })
      }
      setEditPhone(false)
    } else {
      setCellPhoneMsg('Número de telefone inválido!')
    }
  }

  function getPlanGroupCategory() {
    const categories = {
      [CORPORATE_LIMITED]: 'corporate',
      [CORPORATE_UNLIMITED]: 'corporate',
      [CAPITATION]: 'capitation',
      [ENTERPRISE]: 'enterprise',
    }

    return categories[planGroup.category] || categories.ENTERPRISE
  }

  async function resendPin() {
    try {
      setCellPhoneMsg('')
      setResendPinLoading(true)
      setPinSent(false)
      setPinError(false)
      setPinCode('')
      const postUrl = `${apiEndpoints.ACCOUNTS}${email}/pin/`
      const token = await executeRecaptcha('signup')
      const params = {
        recaptcha_token: token,
      }

      const response = await axios.post(postUrl, params)

      if (response.status === 'OK') {
        setPinSent(true)
      }

    } catch(error) {
      setCellPhoneMsg('O Pin não foi enviado corretamente. Tente novamente.')
      setPinSent(false)
    } finally {
      setResendPinLoading(false)
    }
  }

  async function handlePin() {
    setIsValid(false)
    setPinSent(false)
    setShowPin(false)

    const postUrl = `${apiEndpoints.ACCOUNTS}${email}/pin/`

    const token = await executeRecaptcha('signup')

    const params = {
      recaptcha_token: token,
    }

    axios.post(postUrl, params)
      .then(response => {
        if (response.status === 'OK') {
          setPinSent(true)
          setShowPin(true)
        }
      })
      .catch(err => {
        dispatch(asyncError(err)).then(() => {
          setCellPhoneMsg('O Pin não foi enviado corretamente. Tente novamente.')
          setPinSent(false)
          onEditPhone()
        })
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  function onSubmit() {
    setPinError(false)
    setPinSent(false)
    setIsLoading(true)
    dispatch(clearPersons())

    const getUrl = `${apiEndpoints.ACCOUNTS}${email}/pin/${pinCode}/`

    axios.get(getUrl)
      .then(response => {
        if (response.status === 'OK') {

          dispatch(authLogin(
            { username: email, password: password },
            isEnterprise && !hasSimpleSignUp ? false : REGISTER_SUCCESS,
            true
          ))
        }
      })
      .catch(err => {
        dispatch(asyncError(err)).then(() => {
          setPinError(true)
          setPinCode('')
        })

        setIsLoading(false)
      })
  }

  return (
    <Register
      showStepper={true}
      showLoginInfo={false}
      currentStep={stepsList.PHONE_CHECK}
    >
      <Row justify="flex-start">
        <Typography
          center
          variant = 'content2'
        >
          Informe seu número de celular. Será enviado um PIN de segurança para o número cadastrado:
        </Typography>
      </Row>

      <Row justify="center" margin='2.5rem 0 0'>
        <Column>
          <PhoneInput
            cellPhone={cellPhone}
            defaultCountry='BR'
            disabled={disableInput}
            onChangePhone={onCellChange}
            data-cy='signup-input-phone'
          />

          {cellPhoneMsg 
            ? (
              <Typography customClassName='cellphoneMessageError' variant='content2' color="danger">
                {cellPhoneMsg}
              </Typography>
            )
            : null
          }
          
          {
            showPin ? (
              <Column>
                
                {pinSent || resendPinLoading || pinError
                  ? <PinSendedMessage hasPinError={pinError} resendPinLoading={resendPinLoading} />
                  : null
                }
                
                <Row justify="center" padding='10px 0' data-cy='signup-pin-input'>
                  <Pin 
                    handlePinInputChange={handlePinInput}
                    onClickEditPhoneNumber={onEditPhone}
                    onClickResendPin={resendPin}
                    resendPinLoading={resendPinLoading}
                    hasPinError={pinError}
                    data-cy='signup-input-pin'
                  />

                  <Row padding="2rem 0 0 0">
                    
                    <div className='MessageRequired'>
                      <Typography 
                        italic 
                        variant='content2'
                        customClassName='MessageRequired--title' 
                      >
                        * Preenchimento obrigatório.
                      </Typography>
                    </div>

                    <Button
                      variant='contained'
                      onClick={onSubmit}
                      disabled={pinCode.length !== 4 || isLoading}
                      uppercase={false}
                      inlineStyles={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      data-cy='signup-submit'
                    >
                      {isLoading ?  
                        <Spinner 
                          inlineStyles={{
                            width: '30px',
                            height: '30px',
                            border: '4px solid',
                            borderTopColor: 'transparent',
                          }}
                        /> 
                        : 'Finalizar'
                      }
                    </Button>
                  </Row>
                </Row>
              </Column>
            ) : (
              <Row align='space-between' direction='row' margin='2.5rem 0 0' wrap='nowrap'>
                <Column colWidth={12}>
                  <Row padding="0 5px 0 0">
                    <Button
                      variant='outlined'
                      onClick={onPrevStep}
                      uppercase={false}
                      inlineStyles={{
                        width: '100%'
                      }}
                    >
                      Voltar
                    </Button>
                  </Row>
                </Column>

                <Column colWidth={12}>
                  <Row padding="0 0 0 5px">
                    <Button
                      variant='contained'
                      onClick={onNextStep}
                      disabled={!isValid || isLoading}
                      uppercase={false}
                      inlineStyles={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      data-cy='signup-next-step'
                    >
                      {isLoading
                        ? (
                          <Spinner 
                            inlineStyles={{
                              width: '30px',
                              height: '30px',
                              border: '4px solid',
                              borderTopColor: 'transparent',
                            }}
                          /> 
                        ) : 'Próximo'
                      } 
                    </Button>
                  </Row>
                </Column>
              </Row>
            )
          }
        </Column>
      </Row>
    </Register>
  )
}

export default PhoneCheck
